import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant, TENANTS } from '../contexts/TenantContext';
import Layout from '../components/Layout';
import OverWithdrawnSection from '../components/data-health/OverWithdrawnSection';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Database, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Info,
  TrendingUp,
  Users,
  FileText,
  DollarSign,
  PieChart,
  Activity,
  RefreshCw,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  X,
  Save,
  Trash2,
  Edit2,
  Percent,
  ShieldAlert,
  Unlock,
  ShoppingCart,
  Download,
  Upload,
  BarChart2
} from 'lucide-react';
import { formatCurrency, formatCurrencyWithCents, formatNumber } from '../lib/formatters';
import SyndicationCartDialog from '../components/dialogs/SyndicationCartDialog';
import DealInlineEditor from '../components/deals/DealInlineEditor';
import UnparseableRefiTransactions from '../components/data-health/UnparseableRefiTransactions';
import MissingIntegrityFieldsSection from '../components/data-health/MissingIntegrityFieldsSection';
import DealsMissingOriginator from '../components/data-health/DealsMissingOriginator';
import {
  TerminalStatusMismatchSection,
  DefaultWriteoffReclassifySection,
} from '../components/data-health/Bl019StatusMismatchSections';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Category tile definitions ─────────────────────────────────
// severity: 'critical' | 'warning' | 'low'
// checks: subset of missing_fields keys returned by /api/data-health/deal-scores
const DEAL_SCORE_CATEGORIES = [
  { id: 'identity',   label: 'Identity',   severity: 'critical', checks: ['has_deal_id'],                            icon: '🪪', desc: 'Missing deal ID' },
  { id: 'financial',  label: 'Financial',  severity: 'critical', checks: ['has_funding_amount','has_payback','has_rate'], icon: '💰', desc: 'Missing amount, payback, or rate' },
  { id: 'people',     label: 'People',     severity: 'warning',  checks: ['has_broker'],                             icon: '👤', desc: 'No broker assigned' },
  { id: 'contact',    label: 'Contact',    severity: 'warning',  checks: ['has_merchant'],                           icon: '🏢', desc: 'No merchant linked' },
  { id: 'dates',      label: 'Dates',      severity: 'warning',  checks: ['has_funding_date'],                       icon: '📅', desc: 'Missing funding date' },
  { id: 'terms',      label: 'Terms',      severity: 'low',      checks: ['has_payment_frequency','has_term'],       icon: '📋', desc: 'Missing frequency or term' },
  { id: 'commission', label: 'Commission', severity: 'low',      checks: ['has_commission'],                         icon: '📊', desc: 'No commission data' },
  { id: 'status',     label: 'Status',     severity: 'low',      checks: ['has_status'],                             icon: '🔖', desc: 'No status set' },
];

const SEV_STYLE = {
  critical: { ring: 'border-red-300 bg-red-50',    badge: 'bg-red-100 text-red-700',    dot: 'bg-red-500',    label: 'Critical' },
  warning:  { ring: 'border-amber-300 bg-amber-50', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400',  label: 'Warning' },
  low:      { ring: 'border-blue-200 bg-blue-50',   badge: 'bg-blue-100 text-blue-700',  dot: 'bg-blue-400',   label: 'Low' },
};

const MISSING_FIELD_SEVERITY = {
  has_deal_id:           'critical',
  has_funding_amount:    'critical',
  has_payback:           'critical',
  has_rate:              'critical',
  has_merchant:          'warning',
  has_broker:            'warning',
  has_funding_date:      'warning',
  has_payment_frequency: 'low',
  has_term:              'low',
  has_commission:        'low',
  has_status:            'low',
};

// Reusable pagination bar
const PaginationBar = ({ total, page, pageSize, onPage }) => {
  const totalPages = Math.ceil(total / pageSize);
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const pageNums = [...new Set([1, totalPages, page, page - 1, page + 1].filter(p => p >= 1 && p <= totalPages))].sort((a, b) => a - b);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50 text-sm" data-testid="deal-scores-pagination">
      <span className="text-gray-500">
        Showing <strong>{start}–{end}</strong> of <strong>{total}</strong> deals
      </span>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(p => Math.max(1, p - 1))} disabled={page === 1}
          className="px-2 py-1 rounded border text-xs hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pagination-prev">
          ‹ Prev
        </button>
        {pageNums.map((p, i) => (
          <React.Fragment key={p}>
            {i > 0 && pageNums[i - 1] < p - 1 && <span className="px-1 text-gray-400">…</span>}
            <button onClick={() => onPage(p)}
              className={`w-8 h-7 rounded border text-xs font-medium transition-colors ${page === p ? 'bg-blue-600 text-white border-blue-600' : 'hover:bg-white text-gray-700'}`}
              data-testid={`pagination-page-${p}`}>
              {p}
            </button>
          </React.Fragment>
        ))}
        <button onClick={() => onPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
          className="px-2 py-1 rounded border text-xs hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pagination-next">
          Next ›
        </button>
      </div>
    </div>
  );
};

// Slide-over panel for fixing issues
const IssueSlideOver = ({ issue, onClose, onFix, token, tenant, onOpenSyndicationCart }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [merchants, setMerchants] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [liveUnlocked, setLiveUnlocked] = useState(false);

  const issueKey = issue?.issue_key;
  const isLiveTenant = tenant === TENANTS.LIVE;

  // Helper to get headers with tenant
  const getHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'X-Tenant': tenant,
    'Content-Type': 'application/json'
  }), [token, tenant]);

  const fetchRecords = useCallback(async () => {
    if (!issueKey) {
      setLoading(false);
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/data-health/issue-records/${issueKey}`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setRecords(data.records || []);
      }
    } catch (error) {
      console.error('Error fetching issue records:', error);
    } finally {
      setLoading(false);
    }
  }, [issueKey, getHeaders]);

  const fetchMerchants = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/data-health/merchants-list`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setMerchants(data.merchants || []);
      }
    } catch (error) {
      console.error('Error fetching merchants:', error);
    }
  }, [getHeaders]);

  const fetchInvestors = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/data-health/investors-list`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setInvestors(data.investors || []);
      }
    } catch (error) {
      console.error('Error fetching investors:', error);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchRecords();
    if (issueKey === 'deals_missing_merchant') {
      fetchMerchants();
    }
    if (issueKey === 'users_unlinked') {
      fetchInvestors();
    }
  }, [issueKey, fetchRecords, fetchMerchants, fetchInvestors]);

  const startEdit = (record, initialValues = null) => {
    // Block editing in live tenant unless explicitly unlocked
    if (isLiveTenant && !liveUnlocked) {
      return;
    }
    setEditingId(record.id || record.deal_uuid);
    setEditValues(initialValues || { ...record });
    setSaveError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({});
    setSaveError(null);
  };

  // Handle save based on issue type
  const handleSave = async (record) => {
    // Extra confirmation for live tenant
    if (isLiveTenant && !liveUnlocked) {
      return;
    }
    
    setSaving(true);
    setSaveError(null);
    try {
      let endpoint;
      let body;

      // Add force_live flag for live tenant
      const forceParam = isLiveTenant ? '?force_live=true' : '';

      if (issueKey === 'syndication_pct_not_100') {
        endpoint = `${API_URL}/api/data-health/fix-syndication-pct/${record.deal_uuid}${forceParam}`;
        body = editValues.syndicators || [];
      } else if (issueKey === 'deals_missing_merchant' || issueKey === 'deals_invalid_funding' || issueKey === 'deals_invalid_rate') {
        endpoint = `${API_URL}/api/data-health/fix-deal/${record.id}${forceParam}`;
        body = { ...editValues };
        delete body.id;
        delete body.deal_id;
        delete body.deal_name;
      } else if (issueKey === 'deals_missing_schedule_type') {
        // Fix payment frequency
        endpoint = `${API_URL}/api/data-health/fix-deal/${record.id}${forceParam}`;
        if (!editValues.payment_frequency) {
          setSaveError('Please select a payment frequency');
          setSaving(false);
          return;
        }
        body = { payment_frequency: editValues.payment_frequency };
      } else if (issueKey === 'deals_missing_funding_amount' || issueKey === 'deals_missing_rate' || issueKey === 'deals_missing_term_days') {
        // Fix deal numeric fields
        endpoint = `${API_URL}/api/data-health/fix-deal/${record.id}${forceParam}`;
        body = { ...editValues };
        delete body.id;
        delete body.deal_id;
        delete body.deal_name;
        delete body.reason;
      } else if (issueKey === 'transactions_missing_reference') {
        // Fix transaction reference
        endpoint = `${API_URL}/api/data-health/fix-transaction/${record.id}${forceParam}`;
        if (!editValues.reference) {
          setSaveError('Please enter or auto-generate a reference');
          setSaving(false);
          return;
        }
        body = { reference: editValues.reference, issue_code: 'MISSING_REFERENCE' };
      } else if (issueKey === 'syndications_invalid_amount') {
        endpoint = `${API_URL}/api/data-health/fix-syndication/${record.id}${forceParam}`;
        body = { advance_amount: editValues.advance_amount };
      } else if (issueKey === 'contacts_no_type') {
        endpoint = `${API_URL}/api/data-health/fix-contact/${record.id}${forceParam}`;
        body = { ...editValues };
        delete body.id;
        delete body.company_name;
      } else if (issueKey === 'users_unlinked') {
        endpoint = `${API_URL}/api/data-health/fix-user/${record.id}?force_live=true`;  // Users always need force
        body = { contact_id: editValues.contact_id };
      }

      if (endpoint) {
        const res = await fetch(endpoint, {
          method: 'PATCH',
          headers: getHeaders(),
          body: JSON.stringify(body)
        });

        const result = await res.json();
        
        if (res.ok && result.success) {
          // Optimistically remove the row
          setRecords(prev => prev.filter(r => (r.id || r.deal_uuid) !== (record.id || record.deal_uuid)));
          onFix();
          setEditingId(null);
          setEditValues({});
        } else {
          // Show error without crashing
          const errorMsg = result.error || result.detail || 'Failed to save changes';
          setSaveError(errorMsg);
          console.error('Save failed:', errorMsg);
        }
      }
    } catch (error) {
      console.error('Error saving fix:', error);
      setSaveError(error.message || 'Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record, collection) => {
    // Extra confirmation for live tenant
    if (isLiveTenant && !liveUnlocked) {
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this record? This cannot be undone.')) {
      return;
    }

    setSaving(true);
    try {
      const forceParam = isLiveTenant ? '?force_live=true' : '';
      const res = await fetch(`${API_URL}/api/data-health/delete-record/${collection}/${record.id}${forceParam}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (res.ok) {
        const result = await res.json();
        if (result.success) {
          setRecords(prev => prev.filter(r => r.id !== record.id));
          onFix();
        } else {
          alert(`Error: ${result.error}`);
        }
      }
    } catch (error) {
      console.error('Error deleting record:', error);
    } finally {
      setSaving(false);
    }
  };

  // Check if editing is allowed
  const canEdit = !isLiveTenant || liveUnlocked;

  // Render syndication percentage issue
  const renderSyndicationPctRow = (record) => {
    const isEditing = editingId === record.deal_uuid;
    
    return (
      <div key={record.deal_uuid} className="border rounded-lg p-4 mb-4 bg-white">
        <div className="flex justify-between items-start mb-3">
          <div>
            <span className="font-mono text-sm text-gray-500">{record.deal_id}</span>
            <h4 className="font-medium">{record.deal_name}</h4>
          </div>
          <div className="text-right">
            <Badge variant={record.is_over_allocated ? 'destructive' : 'outline'} className={record.is_over_allocated ? '' : 'bg-slate-100 text-slate-700'}>
              {record.total_pct}% syndicated
            </Badge>
            <div className="text-xs text-gray-500 mt-1">
              {record.is_over_allocated
                ? `${Math.abs(record.pfs_retained_pct ?? record.difference)}% over-allocated`
                : `PFS retains ${(record.pfs_retained_pct ?? record.difference)}%`}
            </div>
          </div>
        </div>
        
        <div className="border-t pt-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs">
                <th className="text-left pb-2">Syndicator</th>
                <th className="text-right pb-2">Advance</th>
                <th className="text-right pb-2">Ownership %</th>
              </tr>
            </thead>
            <tbody>
              {record.syndicators.map((synd, idx) => (
                <tr key={synd.id} className="border-t">
                  <td className="py-2">{synd.syndicator_name}</td>
                  <td className="py-2 text-right">{formatCurrency(synd.advance_amount)}</td>
                  <td className="py-2 text-right">
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.01"
                        className="w-20 px-2 py-1 border rounded text-right"
                        value={editValues.syndicators?.[idx]?.ownership_percentage ?? synd.ownership_percentage}
                        onChange={(e) => {
                          const newSynds = [...(editValues.syndicators || record.syndicators)];
                          newSynds[idx] = { ...newSynds[idx], ownership_percentage: parseFloat(e.target.value) || 0 };
                          setEditValues({ ...editValues, syndicators: newSynds });
                        }}
                      />
                    ) : (
                      <span>{synd.ownership_percentage}%</span>
                    )}
                  </td>
                </tr>
              ))}
              {(() => {
                const liveTotal = (isEditing ? (editValues.syndicators || record.syndicators) : record.syndicators)
                  .reduce((sum, s) => sum + (s.ownership_percentage || 0), 0);
                const pfsPct = Math.round((100 - liveTotal) * 100) / 100;
                if (pfsPct <= 0) return null;
                return (
                  <tr className="border-t bg-slate-50 text-slate-500 italic"
                      title="PFS's own position — calculated as 100% minus syndicated share. Not a stored syndication record.">
                    <td className="py-2">PFS (retained)</td>
                    <td className="py-2 text-right">—</td>
                    <td className="py-2 text-right">{pfsPct.toFixed(2)}%</td>
                  </tr>
                );
              })()}
            </tbody>
            {isEditing && (
              <tfoot>
                <tr className="border-t font-medium">
                  <td colSpan="2" className="py-2">New Total:</td>
                  <td className="py-2 text-right">
                    {(() => {
                      const t = (editValues.syndicators || record.syndicators).reduce((sum, s) => sum + (s.ownership_percentage || 0), 0);
                      return t <= 100
                        ? `100.00% (${t.toFixed(2)}% syndicated + ${(100 - t).toFixed(2)}% PFS)`
                        : `${t.toFixed(2)}% (over-allocated)`;
                    })()}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
          {isEditing ? (
            <>
              <button
                onClick={() => handleSave(record)}
                disabled={saving}
                className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
              >
                <Save className="h-4 w-4" /> Save
              </button>
              <button onClick={cancelEdit} className="px-3 py-1.5 bg-gray-200 text-sm rounded hover:bg-gray-300">
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => startEdit(record, { syndicators: record.syndicators })}
              disabled={!canEdit}
              className={`px-3 py-1.5 text-white text-sm rounded flex items-center gap-1 ${
                canEdit ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              <Edit2 className="h-4 w-4" /> Edit Percentages
            </button>
          )}
        </div>
      </div>
    );
  };

  // Render duplicate transactions
  const renderDuplicateTransactionRow = (record) => (
    <div key={JSON.stringify(record.group_key)} className="border rounded-lg p-4 mb-4 bg-white">
      <div className="flex justify-between items-start mb-3">
        <div>
          <span className="font-mono text-sm text-gray-500">{record.deal_id_display}</span>
          <h4 className="font-medium">{record.deal_name}</h4>
        </div>
        <Badge variant="destructive">{record.duplicate_count} duplicates</Badge>
      </div>
      <div className="text-sm mb-2">
        <span className="text-gray-500">Reference:</span> {record.group_key.reference || 'N/A'} |
        <span className="text-gray-500 ml-2">Date:</span> {record.group_key.date} |
        <span className="text-gray-500 ml-2">Amount:</span> {formatCurrency(record.group_key.amount)}
      </div>
      <div className="text-xs text-gray-500">
        Transaction IDs: {record.transaction_ids.slice(0, 3).map(id => id.slice(0, 8)).join(', ')}...
      </div>
      <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
        {canEdit ? (
          record.transaction_ids.slice(1).map(id => (
            <button
              key={id}
              onClick={() => handleDelete({ id }, 'transactions')}
              disabled={saving}
              className="px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded hover:bg-red-200 disabled:opacity-50 flex items-center gap-1"
            >
              <Trash2 className="h-4 w-4" /> Delete {id.slice(0, 6)}...
            </button>
          ))
        ) : (
          <span className="text-sm text-gray-400 italic">Unlock editing to delete duplicates</span>
        )}
      </div>
    </div>
  );

  // Render table-based records
  const renderTableRecords = () => {
    const columns = getColumnsForIssue(issueKey);
    
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b">
              {columns.map(col => (
                <th key={col.key} className={`py-3 px-4 text-left text-xs font-medium text-gray-500 ${col.align === 'right' ? 'text-right' : ''}`}>
                  {col.label}
                </th>
              ))}
              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {records.map(record => {
              const isEditing = editingId === record.id;
              return (
                <tr key={record.id} className="border-b hover:bg-gray-50">
                  {columns.map(col => (
                    <td key={col.key} className={`py-3 px-4 ${col.align === 'right' ? 'text-right' : ''}`}>
                      {renderCell(record, col, isEditing)}
                    </td>
                  ))}
                  <td className="py-3 px-4">
                    {renderActions(record, isEditing)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCell = (record, col, isEditing) => {
    const value = record[col.key];
    
    if (isEditing && col.editable) {
      if (col.type === 'currency' || col.type === 'number') {
        return (
          <input
            type="number"
            step={col.type === 'currency' ? '0.01' : '0.001'}
            className="w-24 px-2 py-1 border rounded text-right"
            value={editValues[col.key] ?? value ?? ''}
            onChange={(e) => setEditValues({ ...editValues, [col.key]: parseFloat(e.target.value) || 0 })}
          />
        );
      }
      if (col.type === 'merchant_select') {
        return (
          <select
            className="w-40 px-2 py-1 border rounded"
            value={editValues[col.key] ?? value ?? ''}
            onChange={(e) => setEditValues({ ...editValues, [col.key]: e.target.value })}
          >
            <option value="">Select merchant...</option>
            {merchants.map(m => <option key={m.id} value={m.id}>{m.company_name}</option>)}
          </select>
        );
      }
      if (col.type === 'investor_select') {
        return (
          <select
            className="w-40 px-2 py-1 border rounded"
            value={editValues[col.key] ?? value ?? ''}
            onChange={(e) => setEditValues({ ...editValues, [col.key]: e.target.value })}
          >
            <option value="">Select investor...</option>
            {investors.map(i => <option key={i.id} value={i.id}>{i.company_name}</option>)}
          </select>
        );
      }
      if (col.type === 'payment_frequency_select') {
        return (
          <select
            className="w-40 px-2 py-1 border rounded text-sm"
            value={editValues[col.key] ?? value ?? ''}
            onChange={(e) => setEditValues({ ...editValues, [col.key]: e.target.value })}
            data-testid="payment-frequency-select"
          >
            <option value="">Select frequency...</option>
            <option value="Daily">Daily</option>
            <option value="Weekly">Weekly</option>
            <option value="Bi-Weekly">Bi-Weekly</option>
            <option value="Monthly">Monthly</option>
          </select>
        );
      }
      if (col.type === 'reference_input') {
        return (
          <div className="flex gap-1 items-center">
            <input
              type="text"
              className="w-32 px-2 py-1 border rounded text-sm"
              value={editValues[col.key] ?? value ?? ''}
              onChange={(e) => setEditValues({ ...editValues, [col.key]: e.target.value })}
              placeholder="Enter reference"
              data-testid="reference-input"
            />
            <button
              type="button"
              className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded border"
              onClick={() => {
                // Auto-generate reference based on transaction type and date
                const txType = record.type || 'TXN';
                const dateStr = record.date ? new Date(record.date).toISOString().slice(0, 10).replace(/-/g, '') : Date.now();
                const autoRef = `${txType.toUpperCase().replace(/\s+/g, '')}-${dateStr}-${record.id?.slice(-6) || Math.random().toString(36).slice(2, 8)}`;
                setEditValues({ ...editValues, [col.key]: autoRef });
              }}
              title="Auto-generate reference"
              data-testid="auto-generate-ref"
            >
              Auto
            </button>
          </div>
        );
      }
      if (col.type === 'checkbox') {
        return (
          <input
            type="checkbox"
            className="w-4 h-4"
            checked={editValues[col.key] ?? value ?? false}
            onChange={(e) => setEditValues({ ...editValues, [col.key]: e.target.checked })}
          />
        );
      }
      // Default text input for other editable fields
      return (
        <input
          type="text"
          className="w-32 px-2 py-1 border rounded text-sm"
          value={editValues[col.key] ?? value ?? ''}
          onChange={(e) => setEditValues({ ...editValues, [col.key]: e.target.value })}
        />
      );
    }
    
    // Display mode
    if (col.type === 'currency') return formatCurrency(value);
    if (col.type === 'checkbox') return value ? 'Yes' : 'No';
    if (col.type === 'badge') return <Badge variant={col.variant?.(value) || 'secondary'}>{value}</Badge>;
    if (col.type === 'date' && value) return new Date(value).toLocaleDateString();
    return value || '-';
  };

  const renderActions = (record, isEditing) => {
    // Special handler for deals with no syndicators - show cart button
    if (issueKey === 'deals_no_syndicators' && onOpenSyndicationCart) {
      return (
        <button
          onClick={() => onOpenSyndicationCart(record.id || record.deal_uuid, `Deal #${record.deal_id} – ${record.deal_name}`)}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center gap-1"
          data-testid={`add-syndicators-btn-${record.deal_id}`}
        >
          <ShoppingCart className="h-3.5 w-3.5" /> Add Syndicators
        </button>
      );
    }

    if (issueKey === 'orphan_syndications' || issueKey === 'orphan_transactions') {
      if (!canEdit) {
        return <span className="text-xs text-gray-400">Locked</span>;
      }
      return (
        <button
          onClick={() => handleDelete(record, issueKey === 'orphan_syndications' ? 'syndications' : 'transactions')}
          disabled={saving}
          className="p-1.5 text-red-600 hover:bg-red-100 rounded"
          title="Delete orphan record"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      );
    }

    if (isEditing) {
      return (
        <div className="flex flex-col gap-1">
          <div className="flex gap-1">
            <button 
              onClick={() => handleSave(record)} 
              disabled={saving} 
              className="p-1.5 text-green-600 hover:bg-green-100 rounded disabled:opacity-50"
              title="Save changes"
              data-testid="save-fix-btn"
            >
              {saving ? (
                <div className="h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
            </button>
            <button onClick={cancelEdit} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" disabled={saving}>
              <X className="h-4 w-4" />
            </button>
          </div>
          {saveError && (
            <span className="text-xs text-red-600 max-w-[120px] truncate" title={saveError}>
              {saveError}
            </span>
          )}
        </div>
      );
    }

    if (!canEdit) {
      return <span className="text-xs text-gray-400">Locked</span>;
    }

    return (
      <button 
        onClick={() => startEdit(record)} 
        className="p-1.5 text-blue-600 hover:bg-blue-100 rounded"
        title="Fix this issue"
        data-testid="fix-issue-btn"
      >
        <Edit2 className="h-4 w-4" />
      </button>
    );
  };

  const getColumnsForIssue = (key) => {
    const columnDefs = {
      'deals_no_syndicators': [
        { key: 'deal_id', label: 'Deal ID' },
        { key: 'deal_name', label: 'Deal Name' },
        { key: 'funding_amount', label: 'Funding', type: 'currency', align: 'right' },
        { key: 'status', label: 'Status', type: 'badge' },
        { key: 'reason', label: 'Issue' }
      ],
      'syndication_pct_not_100': [
        { key: 'deal_id', label: 'Deal ID' },
        { key: 'deal_name', label: 'Deal Name' },
        { key: 'total_pct', label: 'Total %', type: 'percent' },
        { key: 'difference', label: 'Diff', type: 'number' },
        { key: 'reason', label: 'Issue' }
      ],
      'deals_missing_merchant': [
        { key: 'deal_id', label: 'Deal ID' },
        { key: 'deal_name', label: 'Deal Name' },
        { key: 'funding_amount', label: 'Funding', type: 'currency', align: 'right' },
        { key: 'company_id', label: 'Merchant', editable: true, type: 'merchant_select' },
        { key: 'reason', label: 'Issue' }
      ],
      'deals_missing_merchant_email': [
        { key: 'deal_id', label: 'Deal ID' },
        { key: 'deal_name', label: 'Deal Name' },
        { key: 'merchant_name', label: 'Merchant' },
        { key: 'reason', label: 'Issue' }
      ],
      'deals_missing_merchant_phone': [
        { key: 'deal_id', label: 'Deal ID' },
        { key: 'deal_name', label: 'Deal Name' },
        { key: 'merchant_name', label: 'Merchant' },
        { key: 'reason', label: 'Issue' }
      ],
      'deals_missing_funding_amount': [
        { key: 'deal_id', label: 'Deal ID' },
        { key: 'deal_name', label: 'Deal Name' },
        { key: 'funding_amount', label: 'Funding', type: 'currency', align: 'right', editable: true },
        { key: 'status', label: 'Status', type: 'badge' },
        { key: 'reason', label: 'Issue' }
      ],
      'deals_missing_rate': [
        { key: 'deal_id', label: 'Deal ID' },
        { key: 'deal_name', label: 'Deal Name' },
        { key: 'rate', label: 'Rate', type: 'number', editable: true },
        { key: 'reason', label: 'Issue' }
      ],
      'deals_missing_term_days': [
        { key: 'deal_id', label: 'Deal ID' },
        { key: 'deal_name', label: 'Deal Name' },
        { key: 'term_days', label: 'Term Days', type: 'number', editable: true },
        { key: 'reason', label: 'Issue' }
      ],
      'deals_missing_schedule_type': [
        { key: 'deal_id', label: 'Deal ID' },
        { key: 'deal_name', label: 'Deal Name' },
        { key: 'payment_frequency', label: 'Payment Frequency', editable: true, type: 'payment_frequency_select' },
        { key: 'reason', label: 'Issue' }
      ],
      'transactions_missing_reference': [
        { key: 'deal_id_display', label: 'Deal ID' },
        { key: 'deal_name', label: 'Deal Name' },
        { key: 'type', label: 'Type' },
        { key: 'amount', label: 'Amount', type: 'currency', align: 'right' },
        { key: 'date', label: 'Date', type: 'date' },
        { key: 'reference', label: 'Reference', editable: true, type: 'reference_input' },
        { key: 'reason', label: 'Issue' }
      ],
      'deals_invalid_funding': [
        { key: 'deal_id', label: 'Deal ID' },
        { key: 'deal_name', label: 'Deal Name' },
        { key: 'funding_amount', label: 'Funding', type: 'currency', align: 'right', editable: true },
        { key: 'payback', label: 'Payback', type: 'currency', align: 'right', editable: true },
        { key: 'reason', label: 'Issue' }
      ],
      'deals_invalid_rate': [
        { key: 'deal_id', label: 'Deal ID' },
        { key: 'deal_name', label: 'Deal Name' },
        { key: 'rate', label: 'Rate', type: 'number', editable: true },
        { key: 'funding_amount', label: 'Funding', type: 'currency', align: 'right' },
        { key: 'reason', label: 'Issue' }
      ],
      'syndications_invalid_amount': [
        { key: 'deal_id_display', label: 'Deal ID' },
        { key: 'deal_name', label: 'Deal Name' },
        { key: 'syndicator_name', label: 'Syndicator' },
        { key: 'advance_amount', label: 'Advance', type: 'currency', align: 'right', editable: true },
        { key: 'reason', label: 'Issue' }
      ],
      'orphan_syndications': [
        { key: 'deal_id', label: 'Invalid Deal ID' },
        { key: 'deal_name', label: 'Status' },
        { key: 'syndicator_name', label: 'Syndicator' },
        { key: 'advance_amount', label: 'Advance', type: 'currency', align: 'right' },
        { key: 'reason', label: 'Issue' }
      ],
      'orphan_transactions': [
        { key: 'deal_id', label: 'Invalid Deal ID' },
        { key: 'deal_name', label: 'Status' },
        { key: 'type', label: 'Type' },
        { key: 'amount', label: 'Amount', type: 'currency', align: 'right' },
        { key: 'reason', label: 'Issue' }
      ],
      'duplicate_transactions': [
        { key: 'deal_id', label: 'Deal ID' },
        { key: 'deal_name', label: 'Deal Name' },
        { key: 'duplicate_count', label: 'Duplicates' },
        { key: 'reason', label: 'Issue' }
      ],
      'contacts_no_type': [
        { key: 'scope', label: 'Scope', type: 'badge' },
        { key: 'company_name', label: 'Company' },
        { key: 'email', label: 'Email' },
        { key: 'is_merchant', label: 'Merchant', type: 'checkbox', editable: true },
        { key: 'is_investor', label: 'Investor', type: 'checkbox', editable: true },
        { key: 'reason', label: 'Issue' }
      ],
      'users_unlinked': [
        { key: 'scope', label: 'Scope', type: 'badge' },
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'contact_id', label: 'Linked Contact', editable: true, type: 'investor_select' },
        { key: 'reason', label: 'Issue' }
      ],
      'duplicate_deals': [
        { key: 'deal_id', label: 'Deal ID' },
        { key: 'deal_name', label: 'Deal Name' },
        { key: 'funding_amount', label: 'Funding', type: 'currency', align: 'right' },
        { key: 'duplicate_group', label: 'Group' },
        { key: 'reason', label: 'Issue' }
      ]
    };
    return columnDefs[key] || [
      { key: 'deal_id', label: 'Deal ID' },
      { key: 'deal_name', label: 'Deal Name' },
      { key: 'issue_type', label: 'Type' },
      { key: 'reason', label: 'Issue' }
    ];
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error': return 'bg-red-50 border-red-200';
      case 'warning': return 'bg-yellow-50 border-yellow-200';
      default: return 'bg-blue-50 border-blue-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'error': return <XCircle className="h-6 w-6 text-red-500" />;
      case 'warning': return <AlertTriangle className="h-6 w-6 text-yellow-500" />;
      default: return <Info className="h-6 w-6 text-blue-500" />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" data-testid="issue-slideover">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-30" onClick={onClose} />
      
      {/* Slide-over panel */}
      <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className={`px-6 py-4 border-b flex items-center justify-between ${getSeverityColor(issue?.severity)}`}>
          <div className="flex items-center gap-3">
            {getSeverityIcon(issue?.severity)}
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">{issue?.category}</h3>
                <Badge variant="default" className="text-xs">
                  LIVE
                </Badge>
              </div>
              <p className="text-sm text-gray-600">{issue?.message}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-full">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Live Tenant Warning */}
        {isLiveTenant && !liveUnlocked && (
          <div className="px-6 py-4 bg-red-50 border-b border-red-200">
            <div className="flex items-center gap-3">
              <ShieldAlert className="h-6 w-6 text-red-500 shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-red-800">Live Data Protection</p>
                <p className="text-sm text-red-600">
                  You are viewing LIVE data. Edits and deletes are disabled by default.
                </p>
              </div>
              <button
                onClick={() => {
                  if (window.confirm('I understand that changes will affect LIVE production data. This action cannot be automatically undone.')) {
                    setLiveUnlocked(true);
                  }
                }}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center gap-2 shrink-0"
              >
                <Unlock className="h-4 w-4" />
                Unlock Editing
              </button>
            </div>
          </div>
        )}

        {/* Live Unlocked Banner */}
        {isLiveTenant && liveUnlocked && (
          <div className="px-6 py-3 bg-amber-100 border-b border-amber-200">
            <div className="flex items-center gap-2">
              <Unlock className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-amber-800 font-medium">Live editing unlocked</span>
              <span className="text-sm text-amber-600">— Changes will affect production data</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <p className="font-medium text-green-700">All records fixed!</p>
              <p className="text-sm text-gray-500">No more records with this issue.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-500">
                  {records.length} record{records.length !== 1 ? 's' : ''} with issues
                </span>
                <Badge variant="outline">{records.length} remaining</Badge>
              </div>
              
              {/* Render based on issue type */}
              {issueKey === 'syndication_pct_not_100' && records.map(renderSyndicationPctRow)}
              {issueKey === 'duplicate_transactions' && records.map(renderDuplicateTransactionRow)}
              {!['syndication_pct_not_100', 'duplicate_transactions'].includes(issueKey) && renderTableRecords()}
              
              {(issueKey === 'orphan_syndications' || issueKey === 'orphan_transactions') && (
                <p className="mt-4 text-xs text-gray-500 p-3 bg-yellow-50 rounded border border-yellow-200">
                  ⚠️ These records reference deals that no longer exist. Delete them to clean up the data.
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
const DataHealthDashboard = () => {
  const { token } = useAuth();
  const { tenant } = useTenant();
  const [summary, setSummary] = useState(null);
  const [issues, setIssues] = useState(null);
  const [topSyndicators, setTopSyndicators] = useState(null);
  const [dealsNot100, setDealsNot100] = useState(null);
  const [bounceIssues, setBounceIssues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [selectedBounceIssue, setSelectedBounceIssue] = useState(null);
  const [bouncePaymentCandidates, setBouncePaymentCandidates] = useState(null);
  const [bounceSearchTerm, setBounceSearchTerm] = useState('');
  const [searchingPayments, setSearchingPayments] = useState(false);
  const [applyingBounce, setApplyingBounce] = useState(false);
  const [clearingBounce, setClearingBounce] = useState(null); // null | queue_id | 'all'
  const [reconciliation, setReconciliation] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [fixingDeals, setFixingDeals] = useState(new Set());
  const [cartDealId, setCartDealId] = useState(null);
  const [cartDealLabel, setCartDealLabel] = useState('');
  const [cartOpen, setCartOpen] = useState(false);

  // Per-deal health score table state
  const [dealScores, setDealScores] = useState(null);
  const [dealScoresLoading, setDealScoresLoading] = useState(false);
  const [dealScoreSort, setDealScoreSort] = useState({ by: 'score', dir: 'asc' });
  const [dealScoreSearch, setDealScoreSearch] = useState('');
  const [dealScoreStatusFilter, setDealScoreStatusFilter] = useState('all');
  const [dealScoreEditId, setDealScoreEditId] = useState(null);
  const [dealScoreEditValues, setDealScoreEditValues] = useState({});
  const [dealScoreSaving, setDealScoreSaving] = useState(false);
  const [csvImportPreview, setCsvImportPreview] = useState(null); // rows to preview
  const [csvImportLoading, setCsvImportLoading] = useState(false);
  const [csvImportApplying, setCsvImportApplying] = useState(false);
  const [showDealScores, setShowDealScores] = useState(true);
  const [dealScorePage, setDealScorePage] = useState(1);
  const [activeCategoryFilter, setActiveCategoryFilter] = useState(null); // category id or null
  const DEAL_SCORES_PAGE_SIZE = 50;
  const csvFileRef = useRef(null);

  // Helper to get headers with tenant
  const getHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'X-Tenant': tenant
  }), [token, tenant]);

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'X-Tenant': tenant,
    'Content-Type': 'application/json'
  }), [token, tenant]);

  const openSyndicationCart = useCallback((dealId, label) => {
    setCartDealId(dealId);
    setCartDealLabel(label || '');
    setCartOpen(true);
  }, []);

  const handleCartSuccess = useCallback(() => {
    // Refresh both issues data and deals-not-100 data
    const headers = getHeaders();
    Promise.all([
      fetch(`${API_URL}/api/data-health/issues`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/data-health/deals-not-100-syndicated`, { headers }).then(r => r.ok ? r.json() : null),
    ]).then(([issuesData, dealsData]) => {
      if (issuesData) setIssues(issuesData);
      if (dealsData) setDealsNot100(dealsData);
    }).catch(console.error);
  }, [getHeaders]);

  const fetchData = useCallback(async () => {
    try {
      const headers = getHeaders();
      
      const [summaryRes, issuesRes, syndicatorsRes, dealsNot100Res, bounceIssuesRes] = await Promise.all([
        fetch(`${API_URL}/api/data-health/summary`, { headers }),
        fetch(`${API_URL}/api/data-health/issues`, { headers }),
        fetch(`${API_URL}/api/data-health/top-syndicators`, { headers }),
        fetch(`${API_URL}/api/data-health/deals-not-100-syndicated`, { headers }),
        fetch(`${API_URL}/api/data-health/bounce-issues`, { headers })
      ]);

      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (issuesRes.ok) setIssues(await issuesRes.json());
      if (syndicatorsRes.ok) setTopSyndicators(await syndicatorsRes.json());
      if (dealsNot100Res.ok) setDealsNot100(await dealsNot100Res.json());
      if (bounceIssuesRes.ok) setBounceIssues(await bounceIssuesRes.json());
    } catch (error) {
      console.error('Error fetching data health:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getHeaders]);

  // Refetch when tenant changes
  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData, tenant]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
    if (showDealScores) fetchDealScores();
  };

  // Per-deal health score handlers
  const fetchDealScores = useCallback(async () => {
    setDealScoresLoading(true);
    try {
      const params = new URLSearchParams({
        sort_by: dealScoreSort.by,
        sort_dir: dealScoreSort.dir,
        limit: 1000,
      });
      if (dealScoreStatusFilter !== 'all') params.set('status_filter', dealScoreStatusFilter);
      if (dealScoreSearch) params.set('search', dealScoreSearch);

      const res = await fetch(`${API_URL}/api/data-health/deal-scores?${params}`, { headers: getHeaders() });
      if (res.ok) {
        setDealScores(await res.json());
        setDealScorePage(1); // reset to first page on new fetch
      }
    } catch (err) {
      console.error('Error fetching deal scores:', err);
    } finally {
      setDealScoresLoading(false);
    }
  }, [getHeaders, dealScoreSort, dealScoreStatusFilter, dealScoreSearch]);

  useEffect(() => {
    if (showDealScores) fetchDealScores();
  }, [showDealScores, fetchDealScores]);

  const handleDealScoreSort = (field) => {
    setDealScorePage(1);
    setDealScoreSort(prev => ({
      by: field,
      dir: prev.by === field && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const startDealScoreEdit = (deal) => {
    setDealScoreEditId(deal.id);
    setDealScoreEditValues({
      deal_id: deal.deal_id,
      funding_amount: deal.funding_amount,
      payback: deal.payback,
      rate: deal.rate,
      payment_frequency: deal.payment_frequency,
      funding_date: deal.funding_date,
      merchant_name: deal.merchant_name,
    });
  };

  const saveDealScoreEdit = async (dealId) => {
    setDealScoreSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/data-health/fix-deal/${dealId}`, {
        method: 'PATCH',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(dealScoreEditValues),
      });
      if (res.ok) {
        setDealScoreEditId(null);
        fetchDealScores();
      } else {
        const err = await res.json();
        alert(err.detail || 'Save failed');
      }
    } catch (err) {
      alert('Save failed');
    } finally {
      setDealScoreSaving(false);
    }
  };

  // CSV Export
  const handleCsvExport = () => {
    if (!dealScores?.deals?.length) return;
    const FIELDS = ['deal_id', 'merchant_name', 'status', 'health_score', 'funding_amount', 'payback', 'rate', 'payment_frequency', 'funding_date', 'missing_fields'];
    const header = FIELDS.join(',');
    const rows = dealScores.deals.map(d =>
      FIELDS.map(f => {
        const v = f === 'missing_fields' ? (d[f] || []).join('|') : (d[f] ?? '');
        return `"${String(v).replace(/"/g, '""')}"`;
      }).join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `deal_health_scores_${tenant}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // CSV Import — parse file and show preview
  const handleCsvImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) { alert('CSV must have a header row and at least one data row'); return; }
      const header = lines[0].split(',').map(h => h.replace(/^"|"$/g, '').trim());
      const rows = lines.slice(1).map(line => {
        // Simple CSV parse (handles quoted fields)
        const vals = [];
        let cur = '';
        let inQuote = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') { inQuote = !inQuote; }
          else if (ch === ',' && !inQuote) { vals.push(cur); cur = ''; }
          else { cur += ch; }
        }
        vals.push(cur);
        const obj = {};
        header.forEach((h, i) => { obj[h] = (vals[i] || '').trim(); });
        return obj;
      });
      // Map CSV id column: first try 'id', then use deal_id to look up
      const preview = rows.map(r => ({
        id: r.id || '',
        deal_id: r.deal_id || '',
        merchant_name: r.merchant_name || '',
        funding_amount: r.funding_amount || '',
        payback: r.payback || '',
        rate: r.rate || '',
        payment_frequency: r.payment_frequency || '',
        funding_date: r.funding_date || '',
      })).filter(r => r.id);
      if (!preview.length) { alert('No rows with a valid "id" column found. Ensure you export first and edit that file.'); return; }
      setCsvImportPreview(preview);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleCsvImportApply = async () => {
    if (!csvImportPreview?.length) return;
    setCsvImportApplying(true);
    try {
      const res = await fetch(`${API_URL}/api/data-health/deal-scores/bulk-update`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(csvImportPreview),
      });
      const result = await res.json();
      if (res.ok) {
        setCsvImportPreview(null);
        alert(`Import complete: ${result.updated} updated, ${result.skipped} skipped${result.errors?.length ? `, ${result.errors.length} errors` : ''}`);
        fetchDealScores(); // auto-refresh scores
      } else {
        alert(result.detail || 'Import failed');
      }
    } catch {
      alert('Import failed');
    } finally {
      setCsvImportApplying(false);
    }
  };

  const handleIssueFix = () => {
    // Refresh only issues data after a fix (tenant-scoped)
    fetch(`${API_URL}/api/data-health/issues`, {
      headers: getHeaders()
    })
      .then(res => res.json())
      .then(data => setIssues(data))
      .catch(console.error);
  };

  // Bounce issue handlers
  const handleSelectBounceIssue = async (issue) => {
    setSelectedBounceIssue(issue);
    setBouncePaymentCandidates(null);
    setBounceSearchTerm('');
    
    try {
      const res = await fetch(
        `${API_URL}/api/data-health/bounce-issues/${issue.id}/payment-candidates`,
        { headers: getHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setBouncePaymentCandidates(data);
      }
    } catch (error) {
      console.error('Error fetching payment candidates:', error);
    }
  };

  const handleSearchPaymentsForBounce = async () => {
    if (!selectedBounceIssue) return;
    
    setSearchingPayments(true);
    try {
      const res = await fetch(
        `${API_URL}/api/data-health/bounce-issues/${selectedBounceIssue.id}/search-payments?search=${encodeURIComponent(bounceSearchTerm)}`,
        { headers: getHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setBouncePaymentCandidates(data);
      }
    } catch (error) {
      console.error('Error searching payments:', error);
    } finally {
      setSearchingPayments(false);
    }
  };

  const handleApplyBounceToPayment = async (paymentId) => {
    if (!selectedBounceIssue) return;
    
    setApplyingBounce(true);
    try {
      const res = await fetch(
        `${API_URL}/api/data-health/bounce-issues/${selectedBounceIssue.id}/apply?payment_id=${paymentId}`,
        {
          method: 'POST',
          headers: getHeaders()
        }
      );
      
      if (res.ok) {
        // Refresh bounce issues
        const bounceRes = await fetch(`${API_URL}/api/data-health/bounce-issues`, { headers: getHeaders() });
        if (bounceRes.ok) setBounceIssues(await bounceRes.json());
        
        setSelectedBounceIssue(null);
        setBouncePaymentCandidates(null);
      } else {
        const error = await res.json();
        alert(`Error: ${error.detail || 'Failed to apply bounce'}`);
      }
    } catch (error) {
      console.error('Error applying bounce:', error);
      alert('Error applying bounce');
    } finally {
      setApplyingBounce(false);
    }
  };

  const handleClearBounceIssue = async (queueId) => {
    setClearingBounce(queueId);
    try {
      const res = await fetch(
        `${API_URL}/api/data-health/bounce-issues/${queueId}/clear`,
        { method: 'POST', headers: getHeaders() }
      );
      if (res.ok) {
        // Refresh bounce issues
        const bounceRes = await fetch(`${API_URL}/api/data-health/bounce-issues`, { headers: getHeaders() });
        if (bounceRes.ok) setBounceIssues(await bounceRes.json());
      } else {
        const error = await res.json();
        alert(`Error: ${error.detail || 'Failed to clear bounce issue'}`);
      }
    } catch (error) {
      console.error('Error clearing bounce issue:', error);
    } finally {
      setClearingBounce(null);
    }
  };

  const handleClearAllBounceIssues = async () => {
    if (!window.confirm(`Clear all ${bounceIssues?.counts?.total || 0} bounce issues? They will no longer appear in this list.`)) return;
    setClearingBounce('all');
    try {
      const res = await fetch(
        `${API_URL}/api/data-health/bounce-issues/clear-all`,
        { method: 'POST', headers: getHeaders() }
      );
      if (res.ok) {
        const result = await res.json();
        // Refresh bounce issues
        const bounceRes = await fetch(`${API_URL}/api/data-health/bounce-issues`, { headers: getHeaders() });
        if (bounceRes.ok) setBounceIssues(await bounceRes.json());
      } else {
        const error = await res.json();
        alert(`Error: ${error.detail || 'Failed to clear bounce issues'}`);
      }
    } catch (error) {
      console.error('Error clearing all bounce issues:', error);
    } finally {
      setClearingBounce(null);
    }
  };

  const fetchReconciliation = async () => {
    setReconLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/data-health/balance-reconciliation`, {
        headers: getHeaders()
      });
      if (res.ok) setReconciliation(await res.json());
    } catch (error) {
      console.error('Error fetching reconciliation:', error);
    } finally {
      setReconLoading(false);
    }
  };

  const handleFixDeal = async (dealUuid) => {
    setFixingDeals(prev => new Set([...prev, dealUuid]));
    try {
      const res = await fetch(`${API_URL}/api/data-health/balance-reconciliation/fix`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ deal_ids: [dealUuid] })
      });
      if (res.ok) {
        await fetchReconciliation();
      } else {
        const error = await res.json();
        alert(`Error: ${error.detail || 'Failed to fix balance'}`);
      }
    } catch (error) {
      console.error('Error fixing deal balance:', error);
    } finally {
      setFixingDeals(prev => { const s = new Set(prev); s.delete(dealUuid); return s; });
    }
  };

  const handleFixAllMismatches = async () => {
    if (!reconciliation?.mismatches?.length) return;
    if (!window.confirm(`Recalculate balances for ${reconciliation.mismatches.length} deals?`)) return;
    const allIds = reconciliation.mismatches.map(m => m.deal_uuid);
    setFixingDeals(new Set(allIds));
    try {
      const res = await fetch(`${API_URL}/api/data-health/balance-reconciliation/fix`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ deal_ids: allIds })
      });
      if (res.ok) {
        await fetchReconciliation();
      } else {
        const error = await res.json();
        alert(`Error: ${error.detail || 'Failed to fix balances'}`);
      }
    } catch (error) {
      console.error('Error fixing all balances:', error);
    } finally {
      setFixingDeals(new Set());
    }
  };

  const getHealthScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getHealthScoreBg = (score) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 70) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'error':
        return <Badge variant="destructive">Error</Badge>;
      case 'warning':
        return <Badge className="bg-yellow-500">Warning</Badge>;
      default:
        return <Badge variant="secondary">Info</Badge>;
    }
  };

  const getCategoryIcon = (category) => {
    if (category.includes('Syndication')) return <Percent className="h-4 w-4" />;
    if (category.includes('Orphan')) return <AlertTriangle className="h-4 w-4" />;
    if (category.includes('Duplicate')) return <FileText className="h-4 w-4" />;
    return <Database className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const isLiveTenant = tenant === TENANTS.LIVE;

  return (
    <Layout>
      <div className="space-y-6" data-testid="data-health-dashboard">
        {/* Live Tenant Warning Banner */}
        {isLiveTenant && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-3">
            <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0" />
            <div className="flex-1">
              <span className="font-medium text-amber-800">Viewing Live Data</span>
              <span className="text-amber-700 ml-2">— Inline edits require explicit unlock</span>
            </div>
            <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
              LIVE
            </Badge>
          </div>
        )}

        {/* Validation Mode Banner */}
        {!isLiveTenant && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-3">
            <Database className="h-5 w-5 text-blue-600 shrink-0" />
            <div className="flex-1">
              <span className="font-medium text-blue-800">Viewing Validation Data</span>
              <span className="text-blue-700 ml-2">— Safe to edit without affecting live</span>
            </div>
            <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
              VALIDATION
            </Badge>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Data Health Dashboard</h1>
            <p className="text-gray-500 text-sm mt-1">
              Monitor data quality, integrity, and system health
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* OVER-WITHDRAWN SYNDICATOR POSITIONS */}
        <OverWithdrawnSection />

        {/* Health Score Card */}
        {issues && (
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`text-5xl font-bold ${getHealthScoreColor(issues.health_score)}`}>
                    {issues.health_score}
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-gray-700">Health Score</p>
                    <p className="text-sm text-gray-500 capitalize">
                      Status: {issues.health_status.replace('_', ' ')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{issues.total_errors}</div>
                    <div className="text-xs text-gray-500">Errors</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">{issues.total_warnings}</div>
                    <div className="text-xs text-gray-500">Warnings</div>
                  </div>
                </div>
              </div>
              <Progress 
                value={issues.health_score} 
                className={`mt-4 h-2 ${getHealthScoreBg(issues.health_score)}`}
              />
            </CardContent>
          </Card>
        )}

        {/* Collection Stats */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {Object.entries(summary.collections).map(([name, count]) => (
              <Card key={name} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Database className="h-4 w-4 text-blue-500" />
                    <span className="text-xs text-gray-500 capitalize">{name}</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{formatNumber(count)}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Data Issues & Warnings - Main Feature */}
        {issues && (issues.issues.length > 0 || issues.warnings.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                Data Issues & Warnings
              </CardTitle>
              <CardDescription>
                Click any issue to view affected records and fix them inline
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[...issues.issues, ...issues.warnings].map((issue, index) => (
                  <div 
                    key={issue.issue_key || index}
                    onClick={() => issue.issue_key && setSelectedIssue(issue)}
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md hover:scale-[1.005] ${
                      issue.severity === 'error' ? 'bg-red-50 border-red-200 hover:bg-red-100' :
                      issue.severity === 'warning' ? 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100' :
                      'bg-blue-50 border-blue-200 hover:bg-blue-100'
                    }`}
                    data-testid={`issue-item-${index}`}
                  >
                    {getSeverityIcon(issue.severity)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="flex items-center gap-1 font-medium text-gray-900">
                          {getCategoryIcon(issue.category)}
                          {issue.category}
                        </span>
                        {getSeverityBadge(issue.severity)}
                        <Badge variant="outline" className="text-xs bg-white">Click to fix</Badge>
                      </div>
                      <p className="text-sm text-gray-600">{issue.message}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline">{issue.count} records</Badge>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Clear Message */}
        {issues && issues.issues.length === 0 && issues.warnings.length === 0 && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-8 w-8 text-green-500" />
                <div>
                  <p className="font-medium text-green-800">All Systems Healthy</p>
                  <p className="text-sm text-green-600">No data integrity issues detected</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Financial Summary */}
          {summary && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-500" />
                  Financial Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Total Funding</span>
                    <span className="font-bold text-lg">{formatCurrency(summary.financial_totals.total_funding)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Total Payback Expected</span>
                    <span className="font-bold text-lg">{formatCurrency(summary.financial_totals.total_payback)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Payments Collected</span>
                    <span className="font-bold text-lg text-green-600">{formatCurrency(summary.financial_totals.total_payments_made)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Outstanding Balance</span>
                    <span className="font-bold text-lg text-orange-600">{formatCurrency(summary.financial_totals.total_balance)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Syndication Summary */}
          {summary && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-purple-500" />
                  Syndication Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Total Invested</span>
                    <span className="font-bold text-lg">{formatCurrency(summary.syndication_totals.total_invested)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Total Collected</span>
                    <span className="font-bold text-lg text-green-600">{formatCurrency(summary.syndication_totals.total_collected)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Target Collection</span>
                    <span className="font-bold text-lg">{formatCurrency(summary.syndication_totals.total_target)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Available Balance</span>
                    <span className="font-bold text-lg text-blue-600">{formatCurrencyWithCents(summary.syndication_totals.total_available)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Top Syndicators */}
        {/* Balance Reconciliation Section */}
        <Card data-testid="balance-reconciliation-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-indigo-500" />
                  Balance Reconciliation
                </CardTitle>
                <CardDescription>
                  Compare stored deal balances against dynamically calculated balances from transactions
                </CardDescription>
              </div>
              <button
                onClick={fetchReconciliation}
                disabled={reconLoading}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm"
                data-testid="run-reconciliation-btn"
              >
                <RefreshCw className={`h-4 w-4 ${reconLoading ? 'animate-spin' : ''}`} />
                {reconLoading ? 'Running...' : reconciliation ? 'Re-Run Check' : 'Run Check'}
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {!reconciliation && !reconLoading && (
              <div className="text-center py-8 text-gray-400">
                <Activity className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Click "Run Check" to compare stored vs calculated balances</p>
              </div>
            )}
            {reconLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <span className="ml-3 text-sm text-gray-500">Analyzing {summary?.collections?.deals || '...'} deals...</span>
              </div>
            )}
            {reconciliation && !reconLoading && (
              <div className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-gray-700" data-testid="recon-total-checked">{reconciliation.total_checked}</div>
                    <div className="text-sm text-gray-500">Deals Checked</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-700" data-testid="recon-total-matched">{reconciliation.total_matched}</div>
                    <div className="text-sm text-green-600">Matched</div>
                  </div>
                  <div className={`p-4 rounded-lg text-center ${reconciliation.total_mismatched > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                    <div className={`text-2xl font-bold ${reconciliation.total_mismatched > 0 ? 'text-red-700' : 'text-green-700'}`} data-testid="recon-total-mismatched">
                      {reconciliation.total_mismatched}
                    </div>
                    <div className={`text-sm ${reconciliation.total_mismatched > 0 ? 'text-red-600' : 'text-green-600'}`}>Mismatched</div>
                  </div>
                </div>

                {reconciliation.total_mismatched === 0 ? (
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle className="h-6 w-6 text-green-500" />
                    <div>
                      <p className="font-medium text-green-800">All Balances Match</p>
                      <p className="text-sm text-green-600">Stored balances match calculated balances for all {reconciliation.total_checked} deals</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-500">
                        {reconciliation.total_mismatched} deal{reconciliation.total_mismatched !== 1 ? 's' : ''} with balance discrepancies — sorted by largest delta
                      </p>
                      <button
                        onClick={handleFixAllMismatches}
                        disabled={fixingDeals.size > 0}
                        className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                        data-testid="fix-all-balances-btn"
                      >
                        {fixingDeals.size > 0 ? `Fixing ${fixingDeals.size}...` : `Fix All (${reconciliation.total_mismatched})`}
                      </button>
                    </div>
                    <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="py-2 px-3 text-left font-medium text-gray-600">Deal</th>
                            <th className="py-2 px-3 text-right font-medium text-gray-600">Status</th>
                            <th className="py-2 px-3 text-right font-medium text-gray-600">Stored Balance</th>
                            <th className="py-2 px-3 text-right font-medium text-gray-600">Calculated</th>
                            <th className="py-2 px-3 text-right font-medium text-gray-600">Delta</th>
                            <th className="py-2 px-3 text-center font-medium text-gray-600">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {reconciliation.mismatches.slice(0, 50).map((m, idx) => (
                            <tr key={m.deal_uuid} className="hover:bg-gray-50" data-testid={`recon-row-${idx}`}>
                              <td className="py-2 px-3">
                                <div className="font-medium text-gray-900">{m.deal_name}</div>
                                <div className="text-xs text-gray-500">#{m.deal_id}</div>
                              </td>
                              <td className="py-2 px-3 text-right">
                                <Badge variant="outline" className="text-xs">{m.status}</Badge>
                              </td>
                              <td className="py-2 px-3 text-right font-mono">{formatCurrencyWithCents(m.stored_balance)}</td>
                              <td className="py-2 px-3 text-right font-mono">{formatCurrencyWithCents(m.calculated_balance)}</td>
                              <td className="py-2 px-3 text-right">
                                <span className={`font-mono font-medium ${m.delta > 0 ? 'text-red-600' : 'text-orange-600'}`}>
                                  {m.delta > 0 ? '+' : ''}{formatCurrencyWithCents(m.delta)}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-center">
                                <button
                                  onClick={() => handleFixDeal(m.deal_uuid)}
                                  disabled={fixingDeals.has(m.deal_uuid)}
                                  className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                                  data-testid={`fix-balance-btn-${idx}`}
                                >
                                  {fixingDeals.has(m.deal_uuid) ? 'Fixing...' : 'Fix'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {reconciliation.mismatches.length > 50 && (
                        <div className="text-center py-2 text-sm text-gray-500 border-t bg-gray-50">
                          Showing 50 of {reconciliation.mismatches.length} mismatches — largest delta first
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Syndicators */}
        {topSyndicators && topSyndicators.top_syndicators.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-500" />
                Top Syndicators by Investment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Rank</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Syndicator</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Invested</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Collected</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Available</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Deals</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">ROI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSyndicators.top_syndicators.map((syndicator, index) => (
                      <tr key={syndicator.syndicator_id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <Badge variant={index < 3 ? "default" : "secondary"}>
                            #{index + 1}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 font-medium">{syndicator.syndicator_name}</td>
                        <td className="py-3 px-4 text-right">{formatCurrency(syndicator.total_invested)}</td>
                        <td className="py-3 px-4 text-right text-green-600">{formatCurrency(syndicator.total_collected)}</td>
                        <td className="py-3 px-4 text-right text-blue-600">{formatCurrencyWithCents(syndicator.available_balance)}</td>
                        <td className="py-3 px-4 text-right">{syndicator.deal_count}</td>
                        <td className="py-3 px-4 text-right">
                          <span className={syndicator.roi >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {syndicator.roi.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Deals Not 100% Syndicated */}
        {dealsNot100 && dealsNot100.deals_not_100_pct > 0 && (
          <Card className="border-l-4 border-l-amber-500" data-testid="deals-not-100-syndicated">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Percent className="h-5 w-5 text-amber-500" />
                  Deals Not 100% Syndicated
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                    {dealsNot100.deals_not_100_pct} deals
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    Informational
                  </Badge>
                </div>
              </div>
              <CardDescription>
                Deals where syndication ownership doesn't total 100% — sorted by severity (largest delta first)
              </CardDescription>
              {/* Breakdown badges */}
              <div className="flex gap-2 mt-2">
                {dealsNot100.breakdown.zero_syndicators > 0 && (
                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                    {dealsNot100.breakdown.zero_syndicators} with 0 syndicators
                  </Badge>
                )}
                {dealsNot100.breakdown.under_100 > 0 && (
                  <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                    {dealsNot100.breakdown.under_100} under 100%
                  </Badge>
                )}
                {dealsNot100.breakdown.over_100 > 0 && (
                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                    {dealsNot100.breakdown.over_100} over 100%
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Deal</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Status</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Funding</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Syndicators</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Total %</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Delta</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-600">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dealsNot100.deals.slice(0, 25).map((deal, idx) => (
                      <tr 
                        key={deal.deal_uuid} 
                        className={`border-b border-gray-100 hover:bg-gray-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                        data-testid={`deal-not-100-row-${deal.deal_id}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">{deal.deal_name}</span>
                            <span className="text-xs text-gray-500">#{deal.deal_id}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Badge variant="outline" className="text-xs">
                            {deal.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right font-mono">
                          {formatCurrency(deal.funding_amount)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={deal.syndicator_count === 0 ? 'text-red-600 font-bold' : ''}>
                            {deal.syndicator_count}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={
                            deal.syndication_total_pct === 0 ? 'text-red-600 font-bold' :
                            deal.syndication_total_pct < 100 ? 'text-orange-600' :
                            deal.syndication_total_pct > 100 ? 'text-purple-600' : 'text-green-600'
                          }>
                            {deal.syndication_total_pct.toFixed(1)}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={
                            deal.delta_from_100 < 0 ? 'text-red-600' : 'text-purple-600'
                          }>
                            {deal.delta_from_100 > 0 ? '+' : ''}{deal.delta_from_100.toFixed(1)}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => openSyndicationCart(deal.deal_uuid, `Deal #${deal.deal_id} – ${deal.deal_name}`)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                              data-testid={`fix-syndication-${deal.deal_id}`}
                            >
                              <ShoppingCart className="h-3 w-3" />
                              Fix
                            </button>
                            <a
                              href={`/deals/${deal.deal_uuid}`}
                              className="inline-flex items-center gap-1 text-gray-500 hover:text-blue-600 text-xs"
                              data-testid={`view-deal-${deal.deal_id}`}
                            >
                              View
                              <ChevronRight className="h-3 w-3" />
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {dealsNot100.deals.length > 25 && (
                  <div className="text-center py-3 text-sm text-gray-500 border-t">
                    Showing 25 of {dealsNot100.deals.length} deals — most severe first
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Deals 100% Syndicated Message */}
        {dealsNot100 && dealsNot100.deals_not_100_pct === 0 && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-500" />
                <div>
                  <p className="font-medium text-green-800">All Deals Fully Syndicated</p>
                  <p className="text-sm text-green-600">
                    {dealsNot100.total_deals} deals are at exactly 100% syndication ownership
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bounce Issues Panel */}
        {bounceIssues && bounceIssues.counts && bounceIssues.counts.total > 0 && (
          <Card className="border-orange-200" data-testid="bounce-issues-panel">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Bounce Issues</CardTitle>
                    <CardDescription>
                      Bounces that couldn't be automatically matched to payments
                    </CardDescription>
                  </div>
                </div>
                <Badge variant="destructive" className="text-lg px-3 py-1">
                  {bounceIssues.counts.total}
                </Badge>
                <button
                  onClick={handleClearAllBounceIssues}
                  disabled={clearingBounce === 'all'}
                  className="ml-2 px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-red-50 hover:text-red-700 border border-gray-200 hover:border-red-200 transition-colors disabled:opacity-50"
                  data-testid="clear-all-bounce-issues-btn"
                >
                  {clearingBounce === 'all' ? 'Clearing...' : 'Clear All'}
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Summary counts */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-700">{bounceIssues.counts.unmatched}</div>
                    <div className="text-sm text-orange-600">Unmatched (no payment found)</div>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-700">{bounceIssues.counts.ambiguous}</div>
                    <div className="text-sm text-yellow-600">Ambiguous (multiple candidates)</div>
                  </div>
                </div>

                {/* Bounce issues list */}
                <div className="border rounded-lg overflow-hidden max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="py-2 px-3 text-left font-medium text-gray-600">Deal</th>
                        <th className="py-2 px-3 text-left font-medium text-gray-600">Date</th>
                        <th className="py-2 px-3 text-right font-medium text-gray-600">Amount</th>
                        <th className="py-2 px-3 text-left font-medium text-gray-600">Type</th>
                        <th className="py-2 px-3 text-center font-medium text-gray-600">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {[...(bounceIssues.unmatched || []), ...(bounceIssues.ambiguous || [])].slice(0, 20).map((issue, idx) => (
                        <tr key={issue.id || idx} className="hover:bg-gray-50" data-testid={`bounce-issue-row-${idx}`}>
                          <td className="py-2 px-3">
                            <div className="font-medium">{issue.deal_id_number || issue.data?.deal_id_number}</div>
                            <div className="text-xs text-gray-500 truncate max-w-[150px]">{issue.deal_name}</div>
                          </td>
                          <td className="py-2 px-3 text-gray-600">{issue.data?.date}</td>
                          <td className="py-2 px-3 text-right font-medium">{formatCurrency(issue.data?.amount)}</td>
                          <td className="py-2 px-3">
                            <Badge 
                              className={issue.queue_type === 'unmatched_bounce_v2' ? 'bg-orange-100 text-orange-800' : 'bg-yellow-100 text-yellow-800'}
                            >
                              {issue.queue_type === 'unmatched_bounce_v2' ? 'Unmatched' : 'Ambiguous'}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleSelectBounceIssue(issue)}
                                className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                                data-testid={`fix-bounce-btn-${idx}`}
                              >
                                Select Payment
                              </button>
                              <button
                                onClick={() => handleClearBounceIssue(issue.id)}
                                disabled={clearingBounce === issue.id}
                                className="px-2 py-1 text-xs text-gray-500 rounded hover:bg-red-50 hover:text-red-600 border border-transparent hover:border-red-200 transition-colors disabled:opacity-50"
                                data-testid={`clear-bounce-btn-${idx}`}
                                title="Clear this bounce issue"
                              >
                                {clearingBounce === issue.id ? '...' : 'Clear'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {bounceIssues.counts.total > 20 && (
                    <div className="text-center py-2 text-sm text-gray-500 border-t bg-gray-50">
                      Showing 20 of {bounceIssues.counts.total} bounce issues
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* No Bounce Issues Message */}
        {bounceIssues && bounceIssues.counts && bounceIssues.counts.total === 0 && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-500" />
                <div>
                  <p className="font-medium text-green-800">No Bounce Issues</p>
                  <p className="text-sm text-green-600">
                    All bounces have been matched to payments successfully
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bounce Payment Selection Modal */}
        {selectedBounceIssue && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="bounce-payment-modal">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Select Payment to Void</h3>
                  <p className="text-sm text-gray-500">
                    Deal {selectedBounceIssue.deal_id_number} - ${selectedBounceIssue.data?.amount} on {selectedBounceIssue.data?.date}
                  </p>
                </div>
                <button onClick={() => { setSelectedBounceIssue(null); setBouncePaymentCandidates(null); setBounceSearchTerm(''); }} className="p-1 hover:bg-gray-100 rounded">
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Search Box */}
              <div className="px-4 py-3 bg-blue-50 border-b">
                <p className="text-xs text-blue-700 mb-2">
                  💡 Can't find the payment? Search by reference, amount, or date:
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Search reference, amount, or date..."
                    value={bounceSearchTerm}
                    onChange={(e) => setBounceSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchPaymentsForBounce()}
                    className="flex-1 px-3 py-2 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="bounce-payment-search-input"
                  />
                  <button
                    onClick={handleSearchPaymentsForBounce}
                    disabled={searchingPayments}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                    data-testid="bounce-payment-search-btn"
                  >
                    {searchingPayments ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>
              
              <div className="p-4 max-h-96 overflow-y-auto">
                {!bouncePaymentCandidates ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : bouncePaymentCandidates.candidates?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No candidate payments found for this deal</p>
                    <p className="text-xs mt-2">Try searching with different terms above</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 mb-3">
                      {bouncePaymentCandidates.total_candidates || bouncePaymentCandidates.total_found} payment(s) found. Select the one to mark as bounced:
                    </p>
                    {bouncePaymentCandidates.candidates?.map((payment, idx) => (
                      <div 
                        key={payment.id} 
                        className={`p-3 border rounded-lg hover:border-blue-500 cursor-pointer ${payment.is_amount_match ? 'border-green-300 bg-green-50' : ''}`}
                        onClick={() => !applyingBounce && handleApplyBounceToPayment(payment.id)}
                        data-testid={`payment-candidate-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{formatCurrency(payment.amount)}</span>
                              {payment.is_amount_match && (
                                <Badge className="bg-green-100 text-green-800 text-xs">Amount Match</Badge>
                              )}
                              <Badge variant="outline" className="text-xs">#{payment.match_rank}</Badge>
                            </div>
                            <div className="text-sm text-gray-500">
                              {payment.date} • {payment.type} • {payment.reference || 'No reference'}
                            </div>
                          </div>
                          <button
                            className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                            disabled={applyingBounce}
                          >
                            {applyingBounce ? 'Applying...' : 'Void This'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t bg-gray-50">
                <button
                  onClick={() => { setSelectedBounceIssue(null); setBouncePaymentCandidates(null); }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-gray-400 pb-4">
          Last updated: {summary?.generated_at ? new Date(summary.generated_at).toLocaleString() : 'N/A'}
        </div>

        {/* ── Per-Deal Health Score Table ─────────────────────── */}
        <Card data-testid="per-deal-health-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="flex items-center gap-2">
                <BarChart2 className="h-5 w-5 text-blue-600" />
                Per-Deal Health Scores
                {dealScores && <Badge variant="outline" className="ml-1">{dealScores.total} deals</Badge>}
              </CardTitle>
            </div>
            {dealScores && showDealScores && (
              <div className="flex items-center gap-4 mt-2 text-sm">
                <span className="text-gray-500">Portfolio avg: <strong className={dealScores.avg_score >= 80 ? 'text-green-600' : dealScores.avg_score >= 60 ? 'text-yellow-600' : 'text-red-600'}>{dealScores.avg_score}</strong>/100</span>
                <span className="text-green-600">{dealScores.perfect_count} perfect</span>
                <span className="text-red-600">{dealScores.critical_count} critical (&lt;50)</span>
              </div>
            )}
          </CardHeader>

          {showDealScores && (
            <CardContent className="p-0">
              {/* Category tiles */}
              {dealScores && (
                <div className="px-4 pt-4 pb-2 border-b bg-white">
                  <div className="grid grid-cols-4 sm:grid-cols-8 gap-2" data-testid="category-tiles">
                    {DEAL_SCORE_CATEGORIES.map(cat => {
                      const affected = dealScores.deals.filter(d =>
                        cat.checks.some(c => (d.missing_fields || []).includes(c))
                      ).length;
                      const isActive = activeCategoryFilter === cat.id;
                      const sev = SEV_STYLE[cat.severity];
                      return (
                        <button
                          key={cat.id}
                          onClick={() => {
                            setActiveCategoryFilter(isActive ? null : cat.id);
                            setDealScorePage(1);
                          }}
                          title={cat.desc}
                          data-testid={`cat-tile-${cat.id}`}
                          className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2.5 text-center transition-all
                            ${isActive ? `${sev.ring} ring-2 ring-offset-1 ring-current/30` : 'border-gray-200 bg-white hover:bg-gray-50'}
                          `}
                        >
                          <span className="text-base leading-none">{cat.icon}</span>
                          <span className={`text-lg font-bold leading-none ${affected === 0 ? 'text-gray-300' : (cat.severity === 'critical' ? 'text-red-600' : cat.severity === 'warning' ? 'text-amber-600' : 'text-blue-600')}`}>
                            {affected}
                          </span>
                          <span className="text-[10px] text-gray-500 font-medium leading-tight">{cat.label}</span>
                          <span className={`h-1.5 w-1.5 rounded-full ${affected === 0 ? 'bg-gray-200' : sev.dot}`} />
                        </button>
                      );
                    })}
                  </div>
                  {activeCategoryFilter && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                      <span>Filtering by: <strong className="text-gray-800">{DEAL_SCORE_CATEGORIES.find(c => c.id === activeCategoryFilter)?.label}</strong></span>
                      <button onClick={() => { setActiveCategoryFilter(null); setDealScorePage(1); }} className="text-blue-600 hover:underline">
                        Clear filter ×
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Controls bar */}
              <div className="px-4 py-3 border-b flex flex-wrap items-center gap-2 bg-gray-50">
                <input
                  type="text"
                  placeholder="Search deal ID or merchant..."
                  value={dealScoreSearch}
                  onChange={e => setDealScoreSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchDealScores()}
                  className="border rounded px-2 py-1 text-sm flex-1 min-w-[180px]"
                  data-testid="deal-score-search"
                />
                <select
                  value={dealScoreStatusFilter}
                  onChange={e => setDealScoreStatusFilter(e.target.value)}
                  className="border rounded px-2 py-1 text-sm bg-white"
                  data-testid="deal-score-status-filter"
                >
                  <option value="all">All statuses</option>
                  <option value="Active">Active</option>
                  <option value="Default">Default</option>
                  <option value="Paid in Full">Paid in Full</option>
                </select>
                <button
                  onClick={fetchDealScores}
                  disabled={dealScoresLoading}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                  data-testid="deal-score-apply-filter"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${dealScoresLoading ? 'animate-spin' : ''}`} />
                  Apply
                </button>
                <div className="flex-1" />
                {/* CSV Export */}
                <button
                  onClick={handleCsvExport}
                  disabled={!dealScores?.deals?.length}
                  className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-100 flex items-center gap-1 disabled:opacity-40"
                  data-testid="csv-export-btn"
                >
                  <Download className="h-3.5 w-3.5" />
                  Export CSV
                </button>
                {/* CSV Import */}
                <button
                  onClick={() => csvFileRef.current?.click()}
                  className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-100 flex items-center gap-1"
                  data-testid="csv-import-btn"
                >
                  <Upload className="h-3.5 w-3.5" />
                  Import CSV
                </button>
                <input
                  ref={csvFileRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleCsvImportFile}
                  data-testid="csv-file-input"
                />
              </div>

              {/* CSV Import Preview Modal */}
              {csvImportPreview && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
                    <div className="p-4 border-b flex items-center justify-between">
                      <h3 className="font-semibold">CSV Import Preview — {csvImportPreview.length} rows</h3>
                      <button onClick={() => setCsvImportPreview(null)} className="p-1 hover:bg-gray-100 rounded">
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="p-3 bg-amber-50 border-b border-amber-200">
                      <p className="text-xs text-amber-700">Review the changes below. Only rows with a valid <code>id</code> will be updated. Empty cells will be ignored (won't overwrite existing values).</p>
                    </div>
                    <div className="overflow-auto flex-1 p-4">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-gray-100 text-gray-600">
                            {['id','deal_id','merchant_name','funding_amount','payback','rate','payment_frequency','funding_date'].map(h => (
                              <th key={h} className="px-2 py-1 text-left border">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {csvImportPreview.slice(0, 50).map((row, i) => (
                            <tr key={i} className="hover:bg-blue-50">
                              {['id','deal_id','merchant_name','funding_amount','payback','rate','payment_frequency','funding_date'].map(f => (
                                <td key={f} className="px-2 py-1 border text-gray-700 max-w-[120px] truncate">{row[f] || <span className="text-gray-300">—</span>}</td>
                              ))}
                            </tr>
                          ))}
                          {csvImportPreview.length > 50 && (
                            <tr><td colSpan={8} className="px-2 py-1 text-center text-gray-400 text-xs">...and {csvImportPreview.length - 50} more rows</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
                      <button onClick={() => setCsvImportPreview(null)} className="px-4 py-2 border rounded text-sm hover:bg-gray-100">Cancel</button>
                      <button
                        onClick={handleCsvImportApply}
                        disabled={csvImportApplying}
                        className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                        data-testid="csv-import-confirm-btn"
                      >
                        {csvImportApplying ? 'Applying...' : `Apply ${csvImportPreview.length} updates`}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Table */}
              {(() => {
                const activeCat = activeCategoryFilter
                  ? DEAL_SCORE_CATEGORIES.find(c => c.id === activeCategoryFilter)
                  : null;
                const filteredDeals = activeCat
                  ? (dealScores?.deals || []).filter(d =>
                      activeCat.checks.some(c => (d.missing_fields || []).includes(c))
                    )
                  : (dealScores?.deals || []);

                if (dealScoresLoading) return (
                  <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
                );
                if (!dealScores) return (
                  <div className="text-center py-12 text-gray-400">Click "Apply" to load deal scores</div>
                );
                if (filteredDeals.length === 0) return (
                  <div className="text-center py-12 text-gray-400">
                    {activeCat ? `No deals missing ${activeCat.label} data` : 'No deals found'}
                  </div>
                );
                return (
                <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="deal-scores-table">
                    <thead>
                      <tr className="border-b bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        {[
                          { key: 'health_score', label: 'Score' },
                          { key: 'deal_id', label: 'Deal ID' },
                          { key: 'merchant_name', label: 'Merchant' },
                          { key: 'status', label: 'Status' },
                          { key: 'funding_amount', label: 'Funding' },
                          { key: 'payback', label: 'Payback' },
                          { key: 'rate', label: 'Rate' },
                          { key: 'payment_frequency', label: 'Frequency' },
                          { key: 'funding_date', label: 'Funded' },
                        ].map(col => (
                          <th
                            key={col.key}
                            className="px-3 py-2 text-left font-medium cursor-pointer select-none hover:bg-gray-100 whitespace-nowrap"
                            onClick={() => handleDealScoreSort(col.key)}
                          >
                            <span className="flex items-center gap-1">
                              {col.label}
                              {dealScoreSort.by === col.key ? (
                                dealScoreSort.dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                              ) : null}
                            </span>
                          </th>
                        ))}
                        <th className="px-3 py-2 text-left font-medium">Missing</th>
                        <th className="px-3 py-2" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredDeals.slice((dealScorePage - 1) * DEAL_SCORES_PAGE_SIZE, dealScorePage * DEAL_SCORES_PAGE_SIZE).map(deal => (
                        <tr key={deal.id} className="hover:bg-gray-50" data-testid={`deal-score-row-${deal.id}`}>
                          {dealScoreEditId === deal.id ? (
                            // Edit mode
                            <>
                              <td className="px-3 py-2">
                                <span className={`font-bold ${deal.health_score >= 80 ? 'text-green-600' : deal.health_score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                                  {deal.health_score}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  className="border rounded px-1 py-0.5 text-xs w-24"
                                  value={dealScoreEditValues.deal_id || ''}
                                  onChange={e => setDealScoreEditValues(v => ({...v, deal_id: e.target.value}))}
                                  data-testid="edit-deal-id-input"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  className="border rounded px-1 py-0.5 text-xs w-32"
                                  value={dealScoreEditValues.merchant_name || ''}
                                  onChange={e => setDealScoreEditValues(v => ({...v, merchant_name: e.target.value}))}
                                />
                              </td>
                              <td className="px-3 py-2 text-gray-500">{deal.status}</td>
                              <td className="px-3 py-2">
                                <input
                                  type="number" className="border rounded px-1 py-0.5 text-xs w-24"
                                  value={dealScoreEditValues.funding_amount || ''}
                                  onChange={e => setDealScoreEditValues(v => ({...v, funding_amount: e.target.value}))}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number" className="border rounded px-1 py-0.5 text-xs w-24"
                                  value={dealScoreEditValues.payback || ''}
                                  onChange={e => setDealScoreEditValues(v => ({...v, payback: e.target.value}))}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number" step="0.00001" className="border rounded px-1 py-0.5 text-xs w-20"
                                  value={dealScoreEditValues.rate || ''}
                                  onChange={e => setDealScoreEditValues(v => ({...v, rate: e.target.value}))}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  className="border rounded px-1 py-0.5 text-xs bg-white"
                                  value={dealScoreEditValues.payment_frequency || ''}
                                  onChange={e => setDealScoreEditValues(v => ({...v, payment_frequency: e.target.value}))}
                                  data-testid="edit-frequency-select"
                                >
                                  <option value="">—</option>
                                  <option value="Daily">Daily</option>
                                  <option value="Weekly">Weekly</option>
                                  <option value="Bi-Weekly">Bi-Weekly</option>
                                  <option value="Monthly">Monthly</option>
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="date" className="border rounded px-1 py-0.5 text-xs w-32"
                                  value={dealScoreEditValues.funding_date ? dealScoreEditValues.funding_date.slice(0,10) : ''}
                                  onChange={e => setDealScoreEditValues(v => ({...v, funding_date: e.target.value}))}
                                />
                              </td>
                              <td className="px-3 py-2" />
                              <td className="px-3 py-2">
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => saveDealScoreEdit(deal.id)}
                                    disabled={dealScoreSaving}
                                    className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                    data-testid="save-deal-score-btn"
                                  >
                                    <Save className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => setDealScoreEditId(null)}
                                    className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            // Read mode
                            <>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <span className={`font-bold text-base ${deal.health_score >= 80 ? 'text-green-600' : deal.health_score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                                    {deal.health_score}
                                  </span>
                                  <div className="w-12 h-1.5 bg-gray-200 rounded overflow-hidden">
                                    <div
                                      className={`h-full rounded ${deal.health_score >= 80 ? 'bg-green-500' : deal.health_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                      style={{ width: `${deal.health_score}%` }}
                                    />
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-2 font-mono text-xs">{deal.deal_id || <span className="text-red-400">missing</span>}</td>
                              <td className="px-3 py-2">{deal.merchant_name || <span className="text-red-400">missing</span>}</td>
                              <td className="px-3 py-2">
                                <span className={`text-xs px-1.5 py-0.5 rounded ${deal.status === 'Active' ? 'bg-green-100 text-green-700' : deal.status === 'Default' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                                  {deal.status || '—'}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right">{deal.funding_amount ? formatCurrency(deal.funding_amount) : <span className="text-red-400">missing</span>}</td>
                              <td className="px-3 py-2 text-right">{deal.payback ? formatCurrency(deal.payback) : <span className="text-red-400">missing</span>}</td>
                              <td className="px-3 py-2 text-right">{deal.rate ? Number(deal.rate).toFixed(5) : <span className="text-red-400">missing</span>}</td>
                              <td className="px-3 py-2">{deal.payment_frequency || <span className="text-red-400">missing</span>}</td>
                              <td className="px-3 py-2">{deal.funding_date ? deal.funding_date.slice(0, 10) : <span className="text-red-400">missing</span>}</td>
                              <td className="px-3 py-2">
                                {deal.missing_fields?.length > 0 && (
                                  <div className="flex flex-wrap gap-0.5">
                                    {deal.missing_fields.map(f => {
                                      const sev = MISSING_FIELD_SEVERITY[f] || 'low';
                                      const style = SEV_STYLE[sev];
                                      return (
                                        <span key={f} className={`text-xs border rounded px-1 ${style.badge} border-current/20`}>
                                          {f.replace('has_', '')}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <button
                                  onClick={() => startDealScoreEdit(deal)}
                                  className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                  data-testid={`edit-deal-btn-${deal.id}`}
                                >
                                  <Edit2 className="h-3.5 w-3.5" />
                                </button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {filteredDeals.length > DEAL_SCORES_PAGE_SIZE && (
                  <PaginationBar
                    total={filteredDeals.length}
                    page={dealScorePage}
                    pageSize={DEAL_SCORES_PAGE_SIZE}
                    onPage={setDealScorePage}
                  />
                )}
                </>
                );  // end IIFE return
              })()} {/* end IIFE */}
            </CardContent>
          )}
        </Card>

        {/* ── Inline Deal Editor + CSV Import ─────────────────────── */}
        <DealInlineEditor token={token} tenant={tenant} />

      </div>

      {/* Slide-over Panel */}
      {selectedIssue && (
        <IssueSlideOver
          issue={selectedIssue}
          onClose={() => setSelectedIssue(null)}
          onFix={handleIssueFix}
          token={token}
          tenant={tenant}
          onOpenSyndicationCart={openSyndicationCart}
        />
      )}

      {/* Syndicator Cart Dialog */}
      <SyndicationCartDialog
        open={cartOpen}
        onOpenChange={setCartOpen}
        dealId={cartDealId}
        dealLabel={cartDealLabel}
        getAuthHeaders={getAuthHeaders}
        onSuccess={handleCartSuccess}
      />

      {/* Unparseable Refi Transactions */}
      <div className="mt-6">
        <UnparseableRefiTransactions />
      </div>

      {/* Deals With Missing Integrity Fields (Feb 2026 Part B) */}
      <MissingIntegrityFieldsSection />

      {/* BL-019 (2026-06-02): status auto-flip warning surfaces */}
      <div className="mt-6 space-y-6">
        <TerminalStatusMismatchSection />
        <DefaultWriteoffReclassifySection />
      </div>

      {/* BL-028j Phase 2 (d): Deals Missing Originator (2026-06-08) */}
      <div className="mt-6">
        <DealsMissingOriginator />
      </div>
    </Layout>
  );
};

export default DataHealthDashboard;
